// @soft-depth
import React from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { alpha, keyframes } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import { Pencil } from 'lucide-react';
import { sanitize } from 'dompurify';
import { pickCopy } from '../../../../utils/localeCopy';

/**
 * 支票精靈「支票版」— the cheque is the form. The payee line carries a pen that
 * opens the payee list, the figures are typed straight into the $ box, and the
 * amount in words writes itself on the cheque as they are typed. What the page
 * gives the client is the payee and the amount in words; it does not teach how
 * to write a cheque, so the date and signature stay empty print.
 *
 * component.tsx keeps every piece of state and hands in the fields it owns (the
 * amount input, the 「其他」 payee input, the copy button), so both views share
 * one set of features. Paper and pen colours are `cheque.*` theme keys.
 */

const COPY = {
  'zh-Hant-HK': {
    change: '更改',
    changePayee: '更改收款人',
    payeeEmpty: '按「更改」選擇收款人',
    wordsEmpty: '在 $ 方格輸入金額'
  },
  zh: {
    change: '更改',
    changePayee: '更改收款人',
    payeeEmpty: '按「更改」选择收款人',
    wordsEmpty: '在 $ 方格输入金额'
  },
  en: {
    change: 'Change',
    changePayee: 'Change the payee',
    payeeEmpty: 'Tap Change to choose the payee',
    wordsEmpty: 'Type the amount in the $ box'
  }
};

// Faint engraving behind the print, as on a real cheque.
const paperSx = (theme: Theme) => ({
  bgcolor: 'cheque.paper',
  backgroundImage: [
    `repeating-linear-gradient(115deg, ${alpha(theme.palette.cheque.guilloche, 0.18)} 0 1px, transparent 1px 7px)`,
    `repeating-linear-gradient(65deg, ${alpha(theme.palette.cheque.guilloche, 0.14)} 0 1px, transparent 1px 9px)`
  ].join(', '),
  border: '1px solid',
  borderColor: 'cheque.edge'
});

// What the adviser can press or type into is in the accent, so it stands out
// from the print. Still empty, it pulses three times to say where to start.
const ringPulse = keyframes`
  0% { box-shadow: 0 0 0 0 var(--cheque-pulse); }
  70% { box-shadow: 0 0 0 10px transparent; }
  100% { box-shadow: 0 0 0 0 transparent; }
`;

const pulseSx = (on: boolean) =>
  on
    ? {
        '--cheque-pulse': (theme: Theme) => alpha(theme.palette.primary.main, 0.45),
        animation: `${ringPulse} 1.6s ease-out 0.4s 3`,
        '@media (prefers-reduced-motion: reduce)': { animation: 'none' }
      }
    : {};

const PRINT = { color: 'cheque.print', textTransform: 'uppercase' as const, letterSpacing: '0.06em' };

export interface ChequeFaceProps {
  locale?: string;
  /** Sanitised before render; insurer payee names carry <br>. */
  payeeHtml: string;
  amountText: string;
  words: string;
  /** Opens the payee list from the pen on the payee line. */
  onPayeeClick?: () => void;
  /** 「其他」: a field to type the payee on the line instead of the printed name. */
  payeeField?: React.ReactNode;
  /** The amount input, typed into the $ box. Without it the box shows amountText. */
  amountField?: React.ReactNode;
  /** Sits at the end of the words line (the copy button). */
  wordsAction?: React.ReactNode;
  /** 給客戶看: larger writing, nothing to press. */
  large?: boolean;
}

/** The printed specimen cheque, written in. */
export const ChequeFace = (props: ChequeFaceProps) => {
  const { locale, payeeHtml, amountText, words, onPayeeClick, payeeField, amountField, wordsAction, large } = props;
  const ui = pickCopy(COPY, locale);
  // One column on a phone, so the writing steps down there too.
  const writing = large ? { xs: 'display1', sm: 'display3' } : { xs: 'h5', sm: 'display1' };
  const wordsWriting = large ? { xs: 'h5', sm: 'display2' } : { xs: 'h6', sm: 'h5' };
  const amountWriting = large ? { xs: 'h4', sm: 'display2' } : 'h5';
  const editable = !large;

  // The size comes from a type variant, which carries its own ink colour; the pen
  // colour sits one element in so the variant cannot override it.
  const printedPayee = payeeHtml ? (
    <Box component="span" sx={{ display: 'block', typography: writing, wordBreak: 'break-word' }}>
      <Box
        component="span"
        data-testid="cheque-face-payee"
        sx={{ color: 'cheque.ink' }}
        dangerouslySetInnerHTML={{ __html: sanitize(payeeHtml) }}
      />
    </Box>
  ) : (
    <Box component="span" sx={{ typography: 'body2', color: 'text.disabled' }}>
      {ui.payeeEmpty}
    </Box>
  );

  return (
    <Box
      data-testid="cheque-face"
      sx={(theme: Theme) => ({
        ...paperSx(theme),
        position: 'relative',
        overflow: 'hidden',
        borderRadius: 1.5,
        boxShadow: 2,
        px: { xs: 2, sm: 3 },
        pt: { xs: 2.5, sm: 3 },
        pb: { xs: 1.5, sm: 2 },
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: '1fr auto' },
        rowGap: { xs: 2, sm: 2.5 },
        columnGap: 3
      })}
    >
      {/* Crossing, top left */}
      <Box
        aria-hidden="true"
        sx={{
          gridColumn: 1,
          gridRow: { xs: 2, sm: 'auto' },
          justifySelf: 'start',
          alignSelf: 'start',
          // Pivots on its lower left corner so the raised end stays inside the paper.
          transform: 'rotate(-10deg)',
          transformOrigin: 'left bottom',
          borderTop: '1.5px solid',
          borderBottom: '1.5px solid',
          borderColor: 'cheque.ink',
          px: 1,
          py: 0.25,
          mt: 4
        }}
      >
        <Typography
          variant="overline"
          component="span"
          sx={{ color: 'cheque.ink', display: 'block', whiteSpace: 'nowrap' }}
        >
          A/C Payee Only 只准入抬頭人賬戶
        </Typography>
      </Box>

      {/* Date, top right: left for the client, printed only. */}
      <Box aria-hidden="true" sx={{ gridColumn: { xs: 1, sm: 2 }, gridRow: { xs: 1, sm: 'auto' }, justifySelf: 'end' }}>
        <Typography variant="overline" component="span" sx={{ ...PRINT, display: 'block', textAlign: 'right' }}>
          日期 Date
        </Typography>
        <Stack direction="row" sx={{ gap: 0.25, mt: 0.25 }}>
          {['D', 'D', 'M', 'M', 'Y', 'Y', 'Y', 'Y'].map((label, index) => (
            <Box
              key={index}
              sx={{
                width: { xs: 14, sm: 18 },
                height: { xs: 18, sm: 22 },
                border: '1px solid',
                borderColor: 'cheque.rule',
                ml: index === 2 || index === 4 ? 0.75 : 0,
                display: 'flex',
                alignItems: 'flex-end',
                justifyContent: 'center',
                typography: 'overline',
                color: (theme: Theme) => alpha(theme.palette.cheque.print, 0.45)
              }}
            >
              {label}
            </Box>
          ))}
        </Stack>
      </Box>

      {/* Pay … or bearer */}
      <Stack
        direction="row"
        sx={{ gridColumn: '1 / -1', alignItems: 'flex-end', columnGap: 1.5, rowGap: 0.5, flexWrap: 'wrap' }}
      >
        <Typography variant="overline" component="span" sx={{ ...PRINT, flexShrink: 0, pb: 0.75 }}>
          支付 Pay
        </Typography>
        <Stack
          direction="row"
          sx={{
            flex: 1,
            // On a phone the payee takes the line and 或持票人 wraps under it.
            minWidth: { xs: '65%', sm: 0 },
            alignItems: 'flex-end',
            gap: 1,
            pb: 0.5,
            borderBottom: '1px solid',
            borderColor: 'cheque.rule'
          }}
        >
          {editable && payeeField ? (
            // 「其他」: the line itself is typed into, so it wears the accent like the $ box.
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                px: 1,
                py: 0.5,
                borderRadius: 1,
                border: '1.5px solid',
                borderColor: 'primary.light',
                bgcolor: 'primary.soft',
                '&:focus-within': { borderColor: 'primary.main', bgcolor: 'background.paper' }
              }}
            >
              {payeeField}
            </Box>
          ) : (
            <Box sx={{ flex: 1, minWidth: 0 }}>{printedPayee}</Box>
          )}
          {editable && onPayeeClick && (
            <Box
              component="button"
              type="button"
              aria-label={ui.changePayee}
              data-testid="cheque-face-payee-edit"
              onClick={onPayeeClick}
              sx={{
                flexShrink: 0,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                height: 32,
                px: 1.5,
                border: 'none',
                borderRadius: 999,
                typography: 'subtitle2',
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                boxShadow: 1,
                cursor: 'pointer',
                transition: 'background-color 120ms ease, transform 120ms ease',
                '&:hover': { bgcolor: 'primary.dark' },
                '&:active': { transform: 'scale(0.95)' },
                '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 },
                '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
                ...pulseSx(!payeeHtml)
              }}
            >
              <Pencil size={14} aria-hidden="true" />
              {ui.change}
            </Box>
          )}
        </Stack>
        <Typography
          variant="overline"
          component="span"
          sx={{
            ...PRINT,
            flexShrink: 0,
            pb: 0.75,
            ml: 'auto',
            textDecoration: 'line-through',
            textDecorationColor: 'currentColor'
          }}
        >
          或持票人 or bearer
        </Typography>
      </Stack>

      {/* The sum of … in words */}
      <Box sx={{ gridColumn: 1, minWidth: 0 }}>
        <Typography variant="overline" component="span" sx={{ ...PRINT, display: 'block' }}>
          金額 The sum of
        </Typography>
        <Stack direction="row" sx={{ alignItems: 'flex-start', gap: 1, mt: 0.5 }}>
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              minHeight: { xs: 52, sm: 64 },
              // Two ruled lines to write on, as printed.
              backgroundImage: (theme: Theme) =>
                `repeating-linear-gradient(to bottom, transparent 0 calc(2em - 1px), ${theme.palette.cheque.rule} calc(2em - 1px) 2em)`,
              typography: wordsWriting,
              lineHeight: '2em',
              wordBreak: 'break-word'
            }}
          >
            {words ? (
              <Box component="span" data-testid="cheque-face-words" sx={{ color: 'cheque.ink' }}>
                {words}
              </Box>
            ) : (
              <Box component="span" sx={{ typography: 'body2', lineHeight: '2em', color: 'text.disabled' }}>
                {ui.wordsEmpty}
              </Box>
            )}
          </Box>
          {editable && words && (
            // The shared copy button, tinted here so it reads as pressable on the paper.
            <Box
              sx={{
                flexShrink: 0,
                '& .MuiIconButton-root': {
                  bgcolor: 'primary.soft',
                  color: 'primary.dark',
                  border: '1px solid',
                  borderColor: 'primary.border'
                },
                '& .MuiIconButton-root:hover': { bgcolor: 'primary.soft', borderColor: 'primary.main' }
              }}
            >
              {wordsAction}
            </Box>
          )}
        </Stack>
      </Box>

      {/* Amount box: typed into directly. */}
      <Box sx={{ gridColumn: { xs: 1, sm: 2 }, alignSelf: 'end' }}>
        <Stack
          direction="row"
          sx={{
            alignItems: 'center',
            gap: 0.75,
            border: '1.5px solid',
            // Typed into on the page, so it wears the accent; printed in 給客戶看.
            borderColor: editable ? 'primary.light' : 'cheque.print',
            borderRadius: 1,
            bgcolor: editable ? 'primary.soft' : 'cheque.paper',
            px: 1.25,
            py: 0.5,
            minWidth: { xs: 0, sm: 220 },
            cursor: editable ? 'text' : 'default',
            transition: 'border-color 120ms ease, box-shadow 120ms ease, background-color 120ms ease',
            '&:hover': editable ? { borderColor: 'primary.main' } : {},
            '&:focus-within': {
              borderColor: 'primary.main',
              bgcolor: 'background.paper',
              boxShadow: (theme: Theme) => `0 0 0 3px ${alpha(theme.palette.primary.main, 0.16)}`
            },
            '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
            ...pulseSx(editable && !amountText)
          }}
        >
          <Typography variant="h5" component="span" sx={{ color: editable ? 'primary.dark' : 'cheque.print' }}>
            $
          </Typography>
          {editable && amountField ? (
            <Box sx={{ flex: 1, minWidth: 0 }}>{amountField}</Box>
          ) : (
            <Box component="span" sx={{ typography: amountWriting, flex: 1, textAlign: 'right' }}>
              <Box
                component="span"
                data-testid="cheque-face-amount"
                sx={{ color: 'cheque.ink', fontVariantNumeric: 'tabular-nums' }}
              >
                {amountText}
              </Box>
            </Box>
          )}
        </Stack>
      </Box>

      {/* Signature: printed only. */}
      <Box aria-hidden="true" sx={{ gridColumn: { xs: 1, sm: 2 }, width: { xs: 160, sm: 220 }, justifySelf: 'end' }}>
        <Box sx={{ height: { xs: 22, sm: 28 }, borderBottom: '1px solid', borderColor: 'cheque.print' }} />
        <Typography
          variant="overline"
          component="span"
          sx={{ ...PRINT, display: 'block', textAlign: 'center', mt: 0.25 }}
        >
          簽署 Signature
        </Typography>
      </Box>

      {/* MICR line: decoration only. */}
      <Box
        aria-hidden="true"
        sx={{
          gridColumn: '1 / -1',
          pt: 1,
          borderTop: '1px dashed',
          borderColor: 'cheque.edge',
          fontFamily: 'ui-monospace, Menlo, Consolas, monospace',
          typography: 'caption',
          letterSpacing: '0.25em',
          color: (theme: Theme) => alpha(theme.palette.cheque.print, 0.55),
          whiteSpace: 'nowrap',
          overflow: 'hidden'
        }}
      >
        ⑈000000⑈ 000⑆000⑆ 000000⑈000
      </Box>
    </Box>
  );
};

export interface ChequeVersionProps extends Omit<ChequeFaceProps, 'large'> {
  /** The language switch, 匯率 and 給客戶看, owned by the page. */
  children: React.ReactNode;
}

const ChequeVersion = (props: ChequeVersionProps) => {
  const { children, ...face } = props;
  return (
    <Stack data-testid="cheque-version" sx={{ gap: 1.5 }}>
      <ChequeFace {...face} />
      <Paper component="section" sx={{ p: 1.5 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} sx={{ alignItems: { xs: 'stretch', sm: 'center' }, gap: 1.5 }}>
          {children}
        </Stack>
      </Paper>
    </Stack>
  );
};

export default ChequeVersion;
